import { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { trpc } from '../lib/trpc';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// Primary color for charts and highlights
const PRIMARY_COLOR = '#5A8A5E';

/**
 * Interface for pending activation users from API.
 */
interface PendingActivationUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  startedAt: string;
  lastActivityAt: string;
  currentStep: string;
  daysStuck: number;
}

/**
 * Interface for funnel stage data from API.
 */
interface FunnelStage {
  stage: string;
  users: number;
  label: string;
  description: string;
}

/**
 * Interface for weekly trend data from API.
 */
interface WeeklyTrendData {
  week: string;
  completions: number;
  dropoffs: number;
  rate: number;
}

/**
 * Interface for weekly activation data from API.
 */
interface WeeklyActivationData {
  week: string;
  activationRate: number;
  target: number;
}

/**
 * Interface for per-step drop-off data from API.
 */
interface OnboardingStepData {
  stepNumber: number;
  stepId: string;
  stepName: string;
  stepType: string;
  usersAtStep: number;
  usersDropped: number;
  dropoffPercentage: number;
  conversionFromStart: number;
}

/**
 * Interface for step-by-step drop-off analytics.
 */
interface StepByStepDropoff {
  steps: OnboardingStepData[];
  totalStartedOnboarding: number;
  totalCompleted: number;
  overallCompletionRate: number;
}

/**
 * Calculates funnel metrics including conversion rates and drop-off percentages.
 */
function calculateFunnelMetrics(stages: FunnelStage[]) {
  const maxUsers = stages[0]?.users ?? 0;
  return stages.map((stage, index) => {
    const conversionFromStart = maxUsers > 0 ? (stage.users / maxUsers) * 100 : 0;
    const dropOffFromPrevious = index > 0 && stages[index - 1].users > 0
      ? ((stages[index - 1].users - stage.users) / stages[index - 1].users) * 100
      : 0;
    const conversionFromPrevious = index > 0 && stages[index - 1].users > 0
      ? (stage.users / stages[index - 1].users) * 100
      : 100;
    return {
      ...stage,
      conversionFromStart,
      dropOffFromPrevious,
      conversionFromPrevious,
    };
  });
}

/**
 * Funnel Stage Card component - displays a single stage in the funnel.
 */
interface FunnelStageProps {
  stage: string;
  users: number;
  label: string;
  description: string;
  conversionFromStart: number;
  conversionFromPrevious: number;
  dropOffFromPrevious: number;
  index: number;
  totalStages: number;
  maxUsers: number;
}

function FunnelStageCard({
  stage,
  users,
  label,
  description,
  conversionFromStart,
  dropOffFromPrevious,
  index,
  totalStages,
  maxUsers,
}: FunnelStageProps) {
  const isLast = index === totalStages - 1;
  const barWidth = (users / maxUsers) * 100;

  return (
    <div style={styles.stageContainer}>
      {/* Stage Card */}
      <div style={styles.stageCard}>
        <div style={styles.stageHeader}>
          <div style={styles.stageNumberBadge}>{index + 1}</div>
          <div style={styles.stageInfo}>
            <h3 style={styles.stageName}>{stage}</h3>
            <p style={styles.stageLabel}>{label}</p>
          </div>
          <div style={styles.stageMetrics}>
            <span style={styles.stageUserCount}>{users.toLocaleString()}</span>
            <span style={styles.stageConversionPercent}>{conversionFromStart.toFixed(1)}%</span>
          </div>
        </div>

        {/* Visual Progress Bar */}
        <div style={styles.stageBarContainer}>
          <div style={styles.stageBarBackground}>
            <div
              style={{
                ...styles.stageBar,
                width: `${barWidth}%`,
                background: isLast
                  ? PRIMARY_COLOR
                  : `linear-gradient(90deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_COLOR}cc 100%)`,
              }}
            />
          </div>
        </div>

        <p style={styles.stageDescription}>{description}</p>
      </div>

      {/* Drop-off Arrow (except for last stage) */}
      {!isLast && (
        <div style={styles.dropOffConnector}>
          <div style={styles.dropOffArrow}>
            <svg width="24" height="40" viewBox="0 0 24 40">
              <path
                d="M12 4 L12 32 M6 26 L12 32 L18 26"
                stroke="#D4D0CA"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div style={styles.dropOffBadge}>
            <span style={styles.dropOffValue}>-{dropOffFromPrevious.toFixed(1)}%</span>
            <span style={styles.dropOffLabel}>drop-off</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Summary Metrics Card component.
 */
interface MetricCardProps {
  icon: string;
  label: string;
  value: string;
  subtext: string;
  status?: 'good' | 'warning' | 'bad';
}

function MetricCard({ icon, label, value, subtext, status = 'good' }: MetricCardProps) {
  const statusColors = {
    good: { bg: 'rgba(90, 138, 94, 0.08)', text: '#5A8A5E', border: 'rgba(90, 138, 94, 0.2)' },
    warning: { bg: 'rgba(155, 123, 60, 0.08)', text: '#9B7B3C', border: 'rgba(155, 123, 60, 0.2)' },
    bad: { bg: 'rgba(139, 90, 80, 0.08)', text: '#8B5A50', border: 'rgba(139, 90, 80, 0.2)' },
  };
  const colors = statusColors[status];

  return (
    <div
      style={{
        ...styles.metricCard,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <span style={styles.metricIcon}>{icon}</span>
      <div style={styles.metricContent}>
        <p style={{ ...styles.metricValue, color: colors.text }}>{value}</p>
        <p style={styles.metricLabel}>{label}</p>
        <p style={styles.metricSubtext}>{subtext}</p>
      </div>
    </div>
  );
}

/**
 * Weekly Trend Chart component.
 */
interface WeeklyTrendChartProps {
  data: WeeklyTrendData[];
}

function WeeklyTrendChart({ data }: WeeklyTrendChartProps) {
  const maxCompletions = Math.max(...data.map(d => d.completions), 1);
  const totalCompletions = data.reduce((sum, d) => sum + d.completions, 0);
  const avgRate = data.length > 0 ? Math.round(data.reduce((sum, d) => sum + d.rate, 0) / data.length) : 0;

  return (
    <div style={styles.trendCard}>
      <div style={styles.trendHeader}>
        <div>
          <h3 style={styles.chartTitle}>Weekly Onboarding Trend</h3>
          <p style={styles.chartSubtitle}>Completions over the last 6 weeks</p>
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: PRIMARY_COLOR }} />
            <span style={styles.legendLabel}>Completions</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#C9A090' }} />
            <span style={styles.legendLabel}>Drop-offs</span>
          </div>
        </div>
      </div>

      <div style={styles.trendChartContainer}>
        <svg width="100%" height="160" viewBox="0 0 400 160" preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 1, 2, 3].map(i => (
            <line
              key={i}
              x1="40"
              y1={i * 40 + 20}
              x2="380"
              y2={i * 40 + 20}
              stroke="rgba(93, 86, 78, 0.06)"
              strokeWidth="1"
            />
          ))}

          {/* Area fill */}
          <path
            d={`M 60 140 ${data.map((d, i) => `L ${60 + i * 55} ${140 - (d.completions / maxCompletions) * 100}`).join(' ')} L ${60 + (data.length - 1) * 55} 140 Z`}
            fill={`${PRIMARY_COLOR}15`}
          />

          {/* Completion line */}
          <path
            d={`M ${data.map((d, i) => `${60 + i * 55} ${140 - (d.completions / maxCompletions) * 100}`).join(' L ')}`}
            fill="none"
            stroke={PRIMARY_COLOR}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {data.map((d, i) => {
            const x = 60 + i * 55;
            const y = 140 - (d.completions / maxCompletions) * 100;
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="6" fill="white" stroke={PRIMARY_COLOR} strokeWidth="2.5" />
                <circle cx={x} cy={y} r="3" fill={PRIMARY_COLOR} />
              </g>
            );
          })}
        </svg>

        {/* X-axis labels */}
        <div style={styles.xAxisLabels}>
          {data.map((d, i) => (
            <div key={i} style={styles.xAxisLabel}>
              <span style={styles.axisLabelText}>{d.week}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div style={styles.trendSummary}>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Total Completions</p>
          <p style={styles.trendSummaryValue}>{totalCompletions.toLocaleString()}</p>
        </div>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Avg Completion Rate</p>
          <p style={{ ...styles.trendSummaryValue, color: avgRate >= 80 ? '#5A8A5E' : '#9B7B3C' }}>
            {avgRate}%
          </p>
        </div>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Trend</p>
          <p style={{ ...styles.trendSummaryValue, color: '#5A8A5E' }}>+15% this week</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Activation Rate KPI Card component.
 * Displays target vs actual activation rate metrics.
 */
interface ActivationRateKPIProps {
  target: number;
  actual: number;
  weekOverWeekChange: number;
}

function ActivationRateKPICard({ target, actual, weekOverWeekChange }: ActivationRateKPIProps) {
  const gap = target - actual;
  const isOnTarget = actual >= target;
  const isImproving = weekOverWeekChange > 0;

  return (
    <div style={styles.activationKPICard}>
      <div style={styles.activationKPIHeader}>
        <h3 style={styles.chartTitle}>Activation Rate Tracking</h3>
        <p style={styles.chartSubtitle}>Target vs Actual Performance</p>
      </div>

      <div style={styles.activationKPIGrid}>
        {/* Target KPI */}
        <div style={styles.activationKPIItem}>
          <div style={styles.activationKPIIconWrapper}>
            <span style={styles.activationKPIIcon}>🎯</span>
          </div>
          <div style={styles.activationKPIContent}>
            <p style={styles.activationKPILabel}>Target</p>
            <p style={{ ...styles.activationKPIValue, color: '#5A6A8A' }}>{target}%</p>
          </div>
        </div>

        {/* Actual KPI */}
        <div style={styles.activationKPIItem}>
          <div style={{
            ...styles.activationKPIIconWrapper,
            background: isOnTarget ? 'rgba(90, 138, 94, 0.1)' : 'rgba(155, 123, 60, 0.1)',
          }}>
            <span style={styles.activationKPIIcon}>📊</span>
          </div>
          <div style={styles.activationKPIContent}>
            <p style={styles.activationKPILabel}>Actual</p>
            <p style={{
              ...styles.activationKPIValue,
              color: isOnTarget ? '#5A8A5E' : '#9B7B3C',
            }}>{actual}%</p>
          </div>
        </div>

        {/* Gap KPI */}
        <div style={styles.activationKPIItem}>
          <div style={{
            ...styles.activationKPIIconWrapper,
            background: isOnTarget ? 'rgba(90, 138, 94, 0.1)' : 'rgba(139, 90, 80, 0.1)',
          }}>
            <span style={styles.activationKPIIcon}>{isOnTarget ? '✓' : '↓'}</span>
          </div>
          <div style={styles.activationKPIContent}>
            <p style={styles.activationKPILabel}>Gap to Target</p>
            <p style={{
              ...styles.activationKPIValue,
              color: isOnTarget ? '#5A8A5E' : '#8B5A50',
            }}>{isOnTarget ? 'On Target' : `-${gap}%`}</p>
          </div>
        </div>

        {/* Week-over-Week Change */}
        <div style={styles.activationKPIItem}>
          <div style={{
            ...styles.activationKPIIconWrapper,
            background: isImproving ? 'rgba(90, 138, 94, 0.1)' : 'rgba(139, 90, 80, 0.1)',
          }}>
            <span style={styles.activationKPIIcon}>{isImproving ? '📈' : '📉'}</span>
          </div>
          <div style={styles.activationKPIContent}>
            <p style={styles.activationKPILabel}>Week-over-Week</p>
            <p style={{
              ...styles.activationKPIValue,
              color: isImproving ? '#5A8A5E' : '#8B5A50',
            }}>{isImproving ? '+' : ''}{weekOverWeekChange.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Status Message */}
      <div style={{
        ...styles.activationStatusBanner,
        background: isOnTarget ? 'rgba(90, 138, 94, 0.06)' : 'rgba(155, 123, 60, 0.06)',
        borderColor: isOnTarget ? 'rgba(90, 138, 94, 0.15)' : 'rgba(155, 123, 60, 0.15)',
      }}>
        <span style={styles.activationStatusIcon}>{isOnTarget ? '🎉' : '💡'}</span>
        <p style={{
          ...styles.activationStatusText,
          color: isOnTarget ? '#5A8A5E' : '#9B7B3C',
        }}>
          {isOnTarget
            ? 'Great job! You are meeting your activation rate target.'
            : `You are ${gap}% below your target. Consider optimizing your onboarding flow to improve activation.`}
        </p>
      </div>
    </div>
  );
}

/**
 * Weekly Activation Trend Chart component.
 * Displays activation rate over time using Recharts with a target reference line.
 */
interface WeeklyActivationTrendChartProps {
  data: WeeklyActivationData[];
  targetRate: number;
}

function WeeklyActivationTrendChart({ data, targetRate }: WeeklyActivationTrendChartProps) {
  const avgActivation = data.length > 0
    ? Math.round(data.reduce((sum, d) => sum + d.activationRate, 0) / data.length)
    : 0;

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { week: string; activationRate: number; target: number } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const gap = data.target - data.activationRate;
      const isOnTarget = data.activationRate >= data.target;
      return (
        <div style={styles.activationTooltip}>
          <p style={styles.activationTooltipTitle}>{data.week}</p>
          <div style={styles.activationTooltipRow}>
            <span style={styles.activationTooltipLabel}>Activation Rate:</span>
            <span style={{
              ...styles.activationTooltipValue,
              color: isOnTarget ? '#5A8A5E' : '#9B7B3C',
            }}>{data.activationRate}%</span>
          </div>
          <div style={styles.activationTooltipRow}>
            <span style={styles.activationTooltipLabel}>Target:</span>
            <span style={styles.activationTooltipValue}>{data.target}%</span>
          </div>
          {!isOnTarget && (
            <div style={styles.activationTooltipGap}>
              <span style={{ color: '#8B5A50' }}>Gap: -{gap}%</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.activationTrendCard}>
      <div style={styles.trendHeader}>
        <div>
          <h3 style={styles.chartTitle}>Weekly Activation Trend</h3>
          <p style={styles.chartSubtitle}>Tracking activation rate vs target over time</p>
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: PRIMARY_COLOR }} />
            <span style={styles.legendLabel}>Activation Rate</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#5A6A8A', opacity: 0.5 }} />
            <span style={styles.legendLabel}>Target (60%)</span>
          </div>
        </div>
      </div>

      {/* Recharts Line Chart */}
      <div style={styles.activationChartContainer}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <XAxis
              dataKey="week"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#9B9285' }}
            />
            <YAxis
              domain={[0, 100]}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#9B9285' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Target reference line */}
            <ReferenceLine
              y={targetRate}
              stroke="#5A6A8A"
              strokeDasharray="6 4"
              strokeWidth={2}
              strokeOpacity={0.5}
            />
            {/* Activation rate line */}
            <Line
              type="monotone"
              dataKey="activationRate"
              stroke={PRIMARY_COLOR}
              strokeWidth={3}
              dot={{
                fill: 'white',
                stroke: PRIMARY_COLOR,
                strokeWidth: 2.5,
                r: 5,
              }}
              activeDot={{
                fill: PRIMARY_COLOR,
                stroke: 'white',
                strokeWidth: 2,
                r: 7,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div style={styles.trendSummary}>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Avg Activation Rate</p>
          <p style={{
            ...styles.trendSummaryValue,
            color: avgActivation >= targetRate ? '#5A8A5E' : '#9B7B3C',
          }}>{avgActivation}%</p>
        </div>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Target Rate</p>
          <p style={{ ...styles.trendSummaryValue, color: '#5A6A8A' }}>{targetRate}%</p>
        </div>
        <div style={styles.trendSummaryItem}>
          <p style={styles.trendSummaryLabel}>Best Week</p>
          <p style={{ ...styles.trendSummaryValue, color: '#5A8A5E' }}>
            {data.length > 0 ? Math.max(...data.map(d => d.activationRate)) : 0}%
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-Step Drop-off Analytics component.
 * Displays granular data on which steps users abandon the onboarding flow.
 */
interface StepByStepDropoffChartProps {
  data: StepByStepDropoff;
}

function StepByStepDropoffChart({ data }: StepByStepDropoffChartProps) {
  if (!data.steps || data.steps.length === 0) {
    return (
      <div style={styles.stepDropoffCard}>
        <div style={styles.stepDropoffHeader}>
          <h3 style={styles.chartTitle}>Per-Step Drop-off Analysis</h3>
          <p style={styles.chartSubtitle}>No onboarding steps configured</p>
        </div>
      </div>
    );
  }

  const maxUsersAtStep = Math.max(...data.steps.map(s => s.usersAtStep), 1);
  const highestDropoffStep = data.steps.reduce(
    (max, step) => step.dropoffPercentage > max.dropoffPercentage ? step : max,
    data.steps[0]
  );

  return (
    <div style={styles.stepDropoffCard}>
      <div style={styles.stepDropoffHeader}>
        <div>
          <h3 style={styles.chartTitle}>Per-Step Drop-off Analysis</h3>
          <p style={styles.chartSubtitle}>Track exactly where users abandon the onboarding flow</p>
        </div>
        <div style={styles.stepDropoffSummary}>
          <div style={styles.stepDropoffSummaryItem}>
            <span style={styles.stepDropoffSummaryValue}>{data.totalStartedOnboarding}</span>
            <span style={styles.stepDropoffSummaryLabel}>Started</span>
          </div>
          <div style={styles.stepDropoffSummaryDivider} />
          <div style={styles.stepDropoffSummaryItem}>
            <span style={{ ...styles.stepDropoffSummaryValue, color: PRIMARY_COLOR }}>{data.totalCompleted}</span>
            <span style={styles.stepDropoffSummaryLabel}>Completed</span>
          </div>
          <div style={styles.stepDropoffSummaryDivider} />
          <div style={styles.stepDropoffSummaryItem}>
            <span style={{ ...styles.stepDropoffSummaryValue, color: data.overallCompletionRate >= 50 ? PRIMARY_COLOR : '#9B7B3C' }}>
              {data.overallCompletionRate}%
            </span>
            <span style={styles.stepDropoffSummaryLabel}>Completion Rate</span>
          </div>
        </div>
      </div>

      {/* Step-by-step breakdown table */}
      <div style={styles.stepDropoffTableContainer}>
        <div style={styles.stepDropoffTableHeader}>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 60px' }}>Step</div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '1' }}>Step Name</div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>Users Reached</div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>Dropped Off</div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>Drop-off %</div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 200px' }}>Funnel Progress</div>
        </div>

        {data.steps.map((step, index) => {
          const barWidth = maxUsersAtStep > 0 ? (step.usersAtStep / maxUsersAtStep) * 100 : 0;
          const isHighestDropoff = step.stepId === highestDropoffStep.stepId && step.dropoffPercentage > 0;
          const dropoffColor = step.dropoffPercentage >= 30 ? '#8B5A50' :
                               step.dropoffPercentage >= 15 ? '#9B7B3C' : '#5A8A5E';

          return (
            <div
              key={step.stepId}
              style={{
                ...styles.stepDropoffTableRow,
                background: isHighestDropoff ? 'rgba(139, 90, 80, 0.04)' : 'transparent',
              }}
            >
              <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 60px' }}>
                <span style={styles.stepNumberBadgeSmall}>{step.stepNumber}</span>
              </div>
              <div style={{ ...styles.stepDropoffTableCell, flex: '1' }}>
                <div style={styles.stepNameContainer}>
                  <span style={styles.stepNameText}>{step.stepName}</span>
                  <span style={styles.stepTypeTag}>{step.stepType}</span>
                  {isHighestDropoff && (
                    <span style={styles.highestDropoffTag}>Highest Drop-off</span>
                  )}
                </div>
              </div>
              <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
                <span style={styles.stepUserCount}>{step.usersAtStep.toLocaleString()}</span>
              </div>
              <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
                <span style={{ ...styles.stepDroppedCount, color: dropoffColor }}>
                  {step.usersDropped > 0 ? `-${step.usersDropped.toLocaleString()}` : '0'}
                </span>
              </div>
              <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
                <span style={{
                  ...styles.stepDropoffPercent,
                  color: dropoffColor,
                }}>
                  {step.dropoffPercentage > 0 ? `${step.dropoffPercentage}%` : '-'}
                </span>
              </div>
              <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 200px' }}>
                <div style={styles.stepProgressContainer}>
                  <div style={styles.stepProgressBarBackground}>
                    <div
                      style={{
                        ...styles.stepProgressBar,
                        width: `${barWidth}%`,
                        background: index === data.steps.length - 1
                          ? PRIMARY_COLOR
                          : `linear-gradient(90deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_COLOR}99 100%)`,
                      }}
                    />
                  </div>
                  <span style={styles.stepConversionPercent}>{step.conversionFromStart}%</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Completed users row */}
        <div style={{ ...styles.stepDropoffTableRow, background: 'rgba(90, 138, 94, 0.04)' }}>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 60px' }}>
            <span style={{ ...styles.stepNumberBadgeSmall, background: 'rgba(90, 138, 94, 0.15)', color: PRIMARY_COLOR }}>✓</span>
          </div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '1' }}>
            <div style={styles.stepNameContainer}>
              <span style={{ ...styles.stepNameText, color: PRIMARY_COLOR, fontWeight: 600 }}>Completed Onboarding</span>
            </div>
          </div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
            <span style={{ ...styles.stepUserCount, color: PRIMARY_COLOR, fontWeight: 600 }}>
              {data.totalCompleted.toLocaleString()}
            </span>
          </div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
            <span style={{ color: PRIMARY_COLOR }}>-</span>
          </div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 100px', textAlign: 'right' }}>
            <span style={{ color: PRIMARY_COLOR }}>-</span>
          </div>
          <div style={{ ...styles.stepDropoffTableCell, flex: '0 0 200px' }}>
            <div style={styles.stepProgressContainer}>
              <div style={styles.stepProgressBarBackground}>
                <div
                  style={{
                    ...styles.stepProgressBar,
                    width: `${maxUsersAtStep > 0 ? (data.totalCompleted / maxUsersAtStep) * 100 : 0}%`,
                    background: PRIMARY_COLOR,
                  }}
                />
              </div>
              <span style={{ ...styles.stepConversionPercent, color: PRIMARY_COLOR, fontWeight: 600 }}>
                {data.overallCompletionRate}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Insights from step data */}
      {highestDropoffStep.dropoffPercentage > 0 && (
        <div style={styles.stepDropoffInsight}>
          <span style={styles.stepDropoffInsightIcon}>💡</span>
          <span style={styles.stepDropoffInsightText}>
            The <strong>{highestDropoffStep.stepName}</strong> step ({highestDropoffStep.stepType}) has the highest drop-off rate at <strong>{highestDropoffStep.dropoffPercentage}%</strong>.
            Consider simplifying this step or reviewing the user experience to improve completion rates.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Default reminder message template for pending activation users.
 */
const DEFAULT_REMINDER_MESSAGE = `Hi! We noticed you started setting up your account but haven't completed the process yet. We're here to help if you have any questions or need assistance. Would you like to continue where you left off?`;

/**
 * Pending Activations Table component.
 * Displays users stuck in onboarding state with ability to send reminder messages.
 */
interface PendingActivationsTableProps {
  users: PendingActivationUser[];
}

function PendingActivationsTable({ users }: PendingActivationsTableProps) {
  const [reminderStatus, setReminderStatus] = useState<Record<string, 'idle' | 'sending' | 'sent' | 'error'>>({});
  const [bulkSendStatus, setBulkSendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [bulkSendResult, setBulkSendResult] = useState<{ successCount: number; failureCount: number } | null>(null);

  const sendMessageMutation = trpc.users.sendMessage.useMutation({
    onSuccess: (_data, variables) => {
      setReminderStatus(prev => ({ ...prev, [variables.userId]: 'sent' }));
      // Reset status after 3 seconds
      setTimeout(() => {
        setReminderStatus(prev => ({ ...prev, [variables.userId]: 'idle' }));
      }, 3000);
    },
    onError: (_error, variables) => {
      setReminderStatus(prev => ({ ...prev, [variables.userId]: 'error' }));
      // Reset status after 3 seconds
      setTimeout(() => {
        setReminderStatus(prev => ({ ...prev, [variables.userId]: 'idle' }));
      }, 3000);
    },
  });

  const bulkSendMessageMutation = trpc.users.bulkSendMessage.useMutation({
    onSuccess: (data) => {
      setBulkSendStatus('success');
      setBulkSendResult({ successCount: data.successCount, failureCount: data.failureCount });
      // Mark individual users as sent/error based on results
      data.results.forEach((result) => {
        setReminderStatus(prev => ({
          ...prev,
          [result.userId]: result.success ? 'sent' : 'error',
        }));
      });
      // Reset status after 5 seconds
      setTimeout(() => {
        setBulkSendStatus('idle');
        setBulkSendResult(null);
        setReminderStatus({});
      }, 5000);
    },
    onError: () => {
      setBulkSendStatus('error');
      // Reset status after 3 seconds
      setTimeout(() => {
        setBulkSendStatus('idle');
        setBulkSendResult(null);
      }, 3000);
    },
  });

  const handleSendReminder = (user: PendingActivationUser) => {
    setReminderStatus(prev => ({ ...prev, [user.id]: 'sending' }));
    sendMessageMutation.mutate({
      userId: user.id,
      content: DEFAULT_REMINDER_MESSAGE,
    });
  };

  const handleSendReminderToAll = () => {
    if (users.length === 0) return;
    setBulkSendStatus('sending');
    // Set all users to sending state
    const sendingStatus: Record<string, 'sending'> = {};
    users.forEach((user) => {
      sendingStatus[user.id] = 'sending';
    });
    setReminderStatus(sendingStatus);
    bulkSendMessageMutation.mutate({
      userIds: users.map((user) => user.id),
      content: DEFAULT_REMINDER_MESSAGE,
    });
  };

  const getButtonState = (userId: string) => {
    return reminderStatus[userId] || 'idle';
  };

  const getUrgencyLevel = (daysStuck: number): 'low' | 'medium' | 'high' => {
    if (daysStuck >= 10) return 'high';
    if (daysStuck >= 5) return 'medium';
    return 'low';
  };

  const urgencyColors = {
    low: { bg: 'rgba(90, 138, 94, 0.08)', text: '#5A8A5E', border: 'rgba(90, 138, 94, 0.2)' },
    medium: { bg: 'rgba(155, 123, 60, 0.08)', text: '#9B7B3C', border: 'rgba(155, 123, 60, 0.2)' },
    high: { bg: 'rgba(139, 90, 80, 0.08)', text: '#8B5A50', border: 'rgba(139, 90, 80, 0.2)' },
  };

  return (
    <div style={styles.pendingActivationsCard}>
      <div style={styles.pendingActivationsHeader}>
        <div>
          <h3 style={styles.chartTitle}>Pending Activations</h3>
          <p style={styles.chartSubtitle}>Users stuck in onboarding state requiring follow-up</p>
        </div>
        <div style={styles.pendingActivationsHeaderRight}>
          <div style={styles.pendingActivationsCount}>
            <span style={styles.pendingCountValue}>{users.length}</span>
            <span style={styles.pendingCountLabel}>users pending</span>
          </div>
          {users.length > 0 && (
            <button
              onClick={handleSendReminderToAll}
              disabled={bulkSendStatus === 'sending' || bulkSendStatus === 'success'}
              style={{
                ...styles.bulkReminderButton,
                ...(bulkSendStatus === 'success' ? styles.bulkReminderButtonSuccess : {}),
                ...(bulkSendStatus === 'error' ? styles.bulkReminderButtonError : {}),
                ...(bulkSendStatus === 'sending' ? styles.bulkReminderButtonSending : {}),
              }}
            >
              {bulkSendStatus === 'idle' && (
                <>
                  <span style={styles.reminderIcon}>📨</span>
                  Send Reminder to All
                </>
              )}
              {bulkSendStatus === 'sending' && (
                <>
                  <span style={styles.spinnerIcon}>⏳</span>
                  Sending to {users.length}...
                </>
              )}
              {bulkSendStatus === 'success' && bulkSendResult && (
                <>
                  <span style={styles.successIcon}>✓</span>
                  Sent! {bulkSendResult.successCount} success{bulkSendResult.failureCount > 0 ? `, ${bulkSendResult.failureCount} failed` : ''}
                </>
              )}
              {bulkSendStatus === 'error' && (
                <>
                  <span style={styles.errorIcon}>✕</span>
                  Failed to send
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <div style={styles.tableHeaderCell}>User</div>
          <div style={styles.tableHeaderCell}>Current Step</div>
          <div style={styles.tableHeaderCell}>Days Stuck</div>
          <div style={styles.tableHeaderCell}>Last Activity</div>
          <div style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Action</div>
        </div>

        {/* Table Rows */}
        {users.map((user) => {
          const urgency = getUrgencyLevel(user.daysStuck);
          const urgencyStyle = urgencyColors[urgency];
          const buttonState = getButtonState(user.id);

          return (
            <div key={user.id} style={styles.tableRow}>
              {/* User Info */}
              <div style={styles.tableCell}>
                <div style={styles.userInfo}>
                  <div style={styles.userAvatar}>
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div style={styles.userDetails}>
                    <span style={styles.userName}>{user.name}</span>
                    <span style={styles.userEmail}>{user.email}</span>
                  </div>
                </div>
              </div>

              {/* Current Step */}
              <div style={styles.tableCell}>
                <span style={styles.stepBadge}>{user.currentStep}</span>
              </div>

              {/* Days Stuck */}
              <div style={styles.tableCell}>
                <span
                  style={{
                    ...styles.urgencyBadge,
                    background: urgencyStyle.bg,
                    color: urgencyStyle.text,
                    border: `1px solid ${urgencyStyle.border}`,
                  }}
                >
                  {user.daysStuck} days
                </span>
              </div>

              {/* Last Activity */}
              <div style={styles.tableCell}>
                <span style={styles.lastActivity}>{user.lastActivityAt}</span>
              </div>

              {/* Action Button */}
              <div style={{ ...styles.tableCell, textAlign: 'right' }}>
                <button
                  onClick={() => handleSendReminder(user)}
                  disabled={buttonState === 'sending' || buttonState === 'sent'}
                  style={{
                    ...styles.reminderButton,
                    ...(buttonState === 'sent' ? styles.reminderButtonSuccess : {}),
                    ...(buttonState === 'error' ? styles.reminderButtonError : {}),
                    ...(buttonState === 'sending' ? styles.reminderButtonSending : {}),
                  }}
                >
                  {buttonState === 'idle' && (
                    <>
                      <span style={styles.reminderIcon}>💬</span>
                      Send Reminder
                    </>
                  )}
                  {buttonState === 'sending' && (
                    <>
                      <span style={styles.spinnerIcon}>⏳</span>
                      Sending...
                    </>
                  )}
                  {buttonState === 'sent' && (
                    <>
                      <span style={styles.successIcon}>✓</span>
                      Sent!
                    </>
                  )}
                  {buttonState === 'error' && (
                    <>
                      <span style={styles.errorIcon}>✕</span>
                      Failed
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div style={styles.pendingActivationsFooter}>
        <div style={styles.footerStat}>
          <span style={styles.footerStatIcon}>🔴</span>
          <span style={styles.footerStatText}>
            <strong>{users.filter(u => u.daysStuck >= 10).length}</strong> high priority ({'>'}10 days)
          </span>
        </div>
        <div style={styles.footerStat}>
          <span style={styles.footerStatIcon}>🟡</span>
          <span style={styles.footerStatText}>
            <strong>{users.filter(u => u.daysStuck >= 5 && u.daysStuck < 10).length}</strong> medium priority (5-10 days)
          </span>
        </div>
        <div style={styles.footerStat}>
          <span style={styles.footerStatIcon}>🟢</span>
          <span style={styles.footerStatText}>
            <strong>{users.filter(u => u.daysStuck < 5).length}</strong> low priority ({'<'}5 days)
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Onboarding Funnel page component.
 *
 * Displays a multi-stage funnel visualization showing user progression
 * through the onboarding flow: Signup -> Onboarding -> Active.
 * Fetches real data from the analytics API.
 */
interface OnboardingFunnelPageProps {
  title?: string;
  embedded?: boolean;
}

export function OnboardingFunnelPage({
  title = 'Onboarding Funnel',
  embedded = false,
}: OnboardingFunnelPageProps) {
  const { currentBotId, currentBot } = useProject();

  // Fetch funnel data from the API
  const { data: funnelData, isLoading, error } = trpc.analytics.engagement.funnel.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  // Guard if no bot selected
  if (!currentBotId) {
    return (
      <div style={styles.emptyState}>
        <span style={styles.emptyIcon}>📊</span>
        <h2 style={styles.emptyTitle}>Select a Bot</h2>
        <p style={styles.emptyText}>Choose a bot from the sidebar to view onboarding funnel analytics.</p>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div style={styles.emptyState}>
        <span style={styles.emptyIcon}>⏳</span>
        <h2 style={styles.emptyTitle}>Loading Analytics</h2>
        <p style={styles.emptyText}>Fetching onboarding funnel data...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={styles.emptyState}>
        <span style={styles.emptyIcon}>❌</span>
        <h2 style={styles.emptyTitle}>Error Loading Data</h2>
        <p style={styles.emptyText}>{error.message || 'Failed to load funnel analytics.'}</p>
      </div>
    );
  }

  // Guard if no data returned
  if (!funnelData) {
    return (
      <div style={styles.emptyState}>
        <span style={styles.emptyIcon}>📊</span>
        <h2 style={styles.emptyTitle}>No Data Available</h2>
        <p style={styles.emptyText}>No funnel data is available for this bot yet.</p>
      </div>
    );
  }

  // Calculate funnel metrics from API data
  const funnelWithMetrics = calculateFunnelMetrics(funnelData.stages);
  const maxUsers = funnelData.stages[0]?.users ?? 0;
  const finalUsers = funnelData.stages[funnelData.stages.length - 1]?.users ?? 0;
  const overallConversion = funnelData.summary.overallConversionRate.toFixed(1);
  const biggestDropOff = funnelWithMetrics.slice(1).reduce(
    (max, stage) => stage.dropOffFromPrevious > max.dropOff ? { stage: stage.stage, dropOff: stage.dropOffFromPrevious } : max,
    { stage: '', dropOff: 0 }
  );

  // Calculate insight about signup to onboarding conversion
  const signupToOnboardingConversion = funnelData.stages.length >= 2 && funnelData.stages[0].users > 0
    ? ((funnelData.stages[1].users / funnelData.stages[0].users) * 100).toFixed(1)
    : '0.0';

  return (
    <div style={styles.container}>
      {(currentBot || !embedded) && (
        <div style={styles.header}>
          {!embedded && (
            <div>
              <h1 style={styles.title}>{title}</h1>
              <p style={styles.subtitle}>Track user progression through the onboarding flow</p>
            </div>
          )}
          {currentBot && (
            <div
              style={{
                ...styles.projectBadge,
                background: `linear-gradient(135deg, ${currentBot.color || '#3b82f6'}20 0%, ${currentBot.color || '#3b82f6'}10 100%)`,
                border: `1px solid ${currentBot.color || '#3b82f6'}30`,
                marginLeft: embedded ? 'auto' : undefined,
              }}
            >
              <span style={styles.projectIcon}>{currentBot.icon || '🤖'}</span>
              <span style={styles.projectName}>{currentBot.name}</span>
            </div>
          )}
        </div>
      )}

      {/* Summary Metrics */}
      <div style={styles.metricsGrid}>
        <MetricCard
          icon="🎯"
          label="Overall Conversion"
          value={`${overallConversion}%`}
          subtext="Signup to Active"
          status={parseFloat(overallConversion) >= 15 ? 'good' : 'warning'}
        />
        <MetricCard
          icon="👥"
          label="Active Users"
          value={finalUsers.toLocaleString()}
          subtext="Completed onboarding"
          status="good"
        />
        <MetricCard
          icon="📉"
          label="Biggest Drop-off"
          value={`${biggestDropOff.dropOff.toFixed(1)}%`}
          subtext={biggestDropOff.stage ? `At ${biggestDropOff.stage} stage` : 'N/A'}
          status={biggestDropOff.dropOff < 40 ? 'warning' : 'bad'}
        />
        <MetricCard
          icon="⏱️"
          label="Avg Time to Active"
          value={funnelData.summary.avgTimeToActive}
          subtext="From signup to active"
          status="good"
        />
      </div>

      {/* Main Funnel Visualization */}
      <div style={styles.funnelSection}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Funnel Stages</h2>
          <p style={styles.sectionSubtitle}>
            Showing user flow from Signup to Active
          </p>
        </div>

        <div style={styles.funnelContainer}>
          {funnelWithMetrics.map((stage, index) => (
            <FunnelStageCard
              key={stage.stage}
              stage={stage.stage}
              users={stage.users}
              label={stage.label}
              description={stage.description}
              conversionFromStart={stage.conversionFromStart}
              conversionFromPrevious={stage.conversionFromPrevious}
              dropOffFromPrevious={stage.dropOffFromPrevious}
              index={index}
              totalStages={funnelData.stages.length}
              maxUsers={maxUsers}
            />
          ))}
        </div>
      </div>

      {/* Weekly Trend */}
      <WeeklyTrendChart data={funnelData.weeklyTrendData} />

      {/* Per-Step Drop-off Analysis */}
      {funnelData.stepByStepDropoff && (
        <>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Per-Step Drop-off Analysis</h2>
            <p style={styles.sectionSubtitle}>
              Granular data on exactly which steps users abandon the onboarding flow
            </p>
          </div>
          <StepByStepDropoffChart data={funnelData.stepByStepDropoff} />
        </>
      )}

      {/* Pending Activations Section */}
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>Pending Activations</h2>
        <p style={styles.sectionSubtitle}>
          Users stuck in the onboarding state who may need follow-up
        </p>
      </div>
      <PendingActivationsTable users={funnelData.pendingActivationUsers} />

      {/* Activation Rate Tracking Section */}
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>Activation Rate Tracking</h2>
        <p style={styles.sectionSubtitle}>
          Monitor activation rates and track progress towards targets
        </p>
      </div>

      {/* Target vs Actual KPI Cards */}
      <ActivationRateKPICard
        target={funnelData.activationMetrics.target}
        actual={funnelData.activationMetrics.actual}
        weekOverWeekChange={funnelData.activationMetrics.weekOverWeekChange}
      />

      {/* Weekly Activation Trend Chart */}
      <WeeklyActivationTrendChart
        data={funnelData.weeklyActivationData}
        targetRate={funnelData.activationMetrics.target}
      />

      {/* Insights Section */}
      <div style={styles.insightsCard}>
        <h3 style={styles.chartTitle}>Key Insights</h3>
        <div style={styles.insightsList}>
          {biggestDropOff.stage && (
            <div style={styles.insightItem}>
              <span style={styles.insightIcon}>💡</span>
              <span style={styles.insightText}>
                Biggest drop-off occurs at <strong>{biggestDropOff.stage}</strong> stage ({biggestDropOff.dropOff.toFixed(1)}% users lost). Consider optimizing this step.
              </span>
            </div>
          )}
          <div style={styles.insightItem}>
            <span style={styles.insightIcon}>📈</span>
            <span style={styles.insightText}>
              <strong>{overallConversion}%</strong> of signups complete the full onboarding flow and become active users.
            </span>
          </div>
          <div style={styles.insightItem}>
            <span style={styles.insightIcon}>🎯</span>
            <span style={styles.insightText}>
              Signup to Onboarding conversion is <strong>{signupToOnboardingConversion}%</strong>. There's room for improvement with better onboarding prompts.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
  },
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '28px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  projectBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 500,
  },
  projectIcon: {
    fontSize: '16px',
  },
  projectName: {
    color: '#5C564E',
  },
  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '24px',
    fontWeight: 400,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
    maxWidth: '300px',
  },
  // Metrics Grid
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
  },
  metricIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  metricContent: {
    flex: 1,
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: 600,
    margin: 0,
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '2px',
  },
  metricSubtext: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  // Funnel Section
  funnelSection: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  sectionHeader: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '4px',
  },
  sectionSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  funnelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  // Stage Card
  stageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stageCard: {
    width: '100%',
    padding: '20px',
    background: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  stageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  stageNumberBadge: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
  },
  stageInfo: {
    flex: 1,
  },
  stageName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '2px',
  },
  stageLabel: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  stageMetrics: {
    textAlign: 'right',
  },
  stageUserCount: {
    display: 'block',
    fontSize: '20px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  stageConversionPercent: {
    fontSize: '12px',
    color: '#5A8A5E',
    fontWeight: 500,
  },
  stageBarContainer: {
    marginBottom: '12px',
  },
  stageBarBackground: {
    height: '12px',
    background: 'rgba(93, 86, 78, 0.06)',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  stageBar: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.5s ease-out',
  },
  stageDescription: {
    fontSize: '12px',
    color: '#706A62',
    margin: 0,
  },
  // Drop-off Connector
  dropOffConnector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  },
  dropOffArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropOffBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    background: 'rgba(201, 160, 144, 0.1)',
    borderRadius: '100px',
  },
  dropOffValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#C9A090',
  },
  dropOffLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  // Trend Chart
  trendCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  trendHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  chartTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '4px',
  },
  chartSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  legend: {
    display: 'flex',
    gap: '16px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
  },
  legendLabel: {
    fontSize: '11px',
    color: '#706A62',
  },
  trendChartContainer: {
    position: 'relative',
    marginBottom: '16px',
  },
  xAxisLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '40px',
    paddingRight: '40px',
    marginTop: '8px',
  },
  xAxisLabel: {
    textAlign: 'center',
    flex: 1,
  },
  axisLabelText: {
    fontSize: '11px',
    color: '#9B9285',
  },
  trendSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  trendSummaryItem: {
    textAlign: 'center',
  },
  trendSummaryLabel: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
    marginBottom: '4px',
  },
  trendSummaryValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  // Insights Card
  insightsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  insightsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '16px',
  },
  insightItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
    background: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '10px',
  },
  insightIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  insightText: {
    fontSize: '13px',
    color: '#5C564E',
    lineHeight: 1.5,
    margin: 0,
  },
  // Activation Rate KPI Card Styles
  activationKPICard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  activationKPIHeader: {
    marginBottom: '20px',
  },
  activationKPIGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '20px',
  },
  activationKPIItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  activationKPIIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'rgba(90, 106, 138, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  activationKPIIcon: {
    fontSize: '18px',
  },
  activationKPIContent: {
    flex: 1,
  },
  activationKPILabel: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
    marginBottom: '4px',
  },
  activationKPIValue: {
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
  },
  activationStatusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 18px',
    borderRadius: '10px',
    border: '1px solid',
  },
  activationStatusIcon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  activationStatusText: {
    fontSize: '13px',
    margin: 0,
    lineHeight: 1.5,
  },
  // Activation Trend Chart Styles
  activationTrendCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  activationChartContainer: {
    width: '100%',
    marginBottom: '16px',
  },
  activationTooltip: {
    background: 'white',
    borderRadius: '10px',
    padding: '12px 16px',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    minWidth: '140px',
  },
  activationTooltipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '8px',
  },
  activationTooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  activationTooltipLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  activationTooltipValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  activationTooltipGap: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
  },
  // Pending Activations Table Styles
  pendingActivationsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  pendingActivationsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  pendingActivationsHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pendingActivationsCount: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
  },
  pendingCountValue: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#9B7B3C',
  },
  pendingCountLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  tableContainer: {
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr',
    gap: '16px',
    padding: '12px 16px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  tableHeaderCell: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#706A62',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr',
    gap: '16px',
    padding: '14px 16px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(93, 86, 78, 0.04)',
    transition: 'background 0.15s ease',
  },
  tableCell: {
    fontSize: '13px',
    color: '#3D3A36',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #5A8A5E20 0%, #5A8A5E10 100%)',
    color: '#5A8A5E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  userEmail: {
    fontSize: '11px',
    color: '#9B9285',
  },
  stepBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    background: 'rgba(93, 86, 78, 0.06)',
    borderRadius: '100px',
    fontSize: '12px',
    color: '#5C564E',
  },
  urgencyBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 500,
  },
  lastActivity: {
    fontSize: '12px',
    color: '#706A62',
  },
  reminderButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    background: PRIMARY_COLOR,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  reminderButtonSuccess: {
    background: '#5A8A5E',
    cursor: 'default',
  },
  reminderButtonError: {
    background: '#8B5A50',
    cursor: 'default',
  },
  reminderButtonSending: {
    background: '#9B7B3C',
    cursor: 'wait',
  },
  bulkReminderButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    background: PRIMARY_COLOR,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
  },
  bulkReminderButtonSuccess: {
    background: '#5A8A5E',
    cursor: 'default',
  },
  bulkReminderButtonError: {
    background: '#8B5A50',
    cursor: 'default',
  },
  bulkReminderButtonSending: {
    background: '#9B7B3C',
    cursor: 'wait',
  },
  reminderIcon: {
    fontSize: '12px',
  },
  spinnerIcon: {
    fontSize: '12px',
  },
  successIcon: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  errorIcon: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  pendingActivationsFooter: {
    display: 'flex',
    gap: '24px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  footerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerStatIcon: {
    fontSize: '12px',
  },
  footerStatText: {
    fontSize: '12px',
    color: '#706A62',
  },
  // Step-by-Step Drop-off Styles
  stepDropoffCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  stepDropoffHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  stepDropoffSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 20px',
    background: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  stepDropoffSummaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  stepDropoffSummaryValue: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  stepDropoffSummaryLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  stepDropoffSummaryDivider: {
    width: '1px',
    height: '32px',
    background: 'rgba(93, 86, 78, 0.1)',
  },
  stepDropoffTableContainer: {
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  stepDropoffTableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  stepDropoffTableCell: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#706A62',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    padding: '0 8px',
  },
  stepDropoffTableRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.04)',
    transition: 'background 0.15s ease',
  },
  stepNumberBadgeSmall: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
  },
  stepNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  stepNameText: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  stepTypeTag: {
    fontSize: '10px',
    padding: '2px 8px',
    borderRadius: '100px',
    background: 'rgba(93, 86, 78, 0.06)',
    color: '#706A62',
    textTransform: 'capitalize' as const,
  },
  highestDropoffTag: {
    fontSize: '10px',
    padding: '2px 8px',
    borderRadius: '100px',
    background: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    fontWeight: 500,
  },
  stepUserCount: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  stepDroppedCount: {
    fontSize: '13px',
    fontWeight: 500,
  },
  stepDropoffPercent: {
    fontSize: '13px',
    fontWeight: 600,
  },
  stepProgressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
  },
  stepProgressBarBackground: {
    flex: 1,
    height: '8px',
    background: 'rgba(93, 86, 78, 0.06)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  stepProgressBar: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease-out',
  },
  stepConversionPercent: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#706A62',
    minWidth: '40px',
    textAlign: 'right' as const,
  },
  stepDropoffInsight: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginTop: '16px',
    padding: '14px 18px',
    background: 'rgba(155, 123, 60, 0.06)',
    borderRadius: '10px',
    border: '1px solid rgba(155, 123, 60, 0.15)',
  },
  stepDropoffInsightIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  stepDropoffInsightText: {
    fontSize: '13px',
    color: '#9B7B3C',
    lineHeight: 1.5,
    margin: 0,
  },
};

export default OnboardingFunnelPage;
